(ns dashboard.components.file-upload.events
  (:require [ajax.core :as ajax]
            [dashboard.components.file-upload.subs :as subs]
            [dashboard.db :as db]
            [dashboard.entity.questionnaire.db :as questionnaire.db]
            [dashboard.notifications :as notifications]
            [dashboard.services.document-list :as documents]
            [dashboard.services.http-config :as http-config]
            [dashboard.services.questionnaire-list :as questionnaire-list]
            [dashboard.ui :as ui]
            [re-frame.core :as rf]))

(rf/reg-event-db
  ::select-files
  (fn [db [_ file-type e]]
    (let [file-list (.. e -target -files)]
      (update-in db
                 [:upload file-type :files]
                 (fn [files]
                   (into files
                         ;; keying by filename keeps duplicates away
                         (map (fn [file]
                                [(.-name file) {:file file
                                                :upload-status :pending}]))
                         file-list))))))

(rf/reg-event-db
  ::reset
  (fn [db [_ file-type]]
    (assoc-in db
              [:upload file-type]
              db/empty-upload-state)))

(rf/reg-event-db
  ::remove-file
  (fn [db [_ file-type file-name]]
    (update-in db [:upload file-type :files] dissoc file-name)))

(rf/reg-event-db
  ::clear-files
  (fn [db [_ file-type]]
    (assoc-in db [:upload file-type :files] {})))

;; File upload procedure

;; Use can select multiple files and when ready, start upload for all of them
;; We take the files selected and one-by-one fetch a upload URL for the file.
;; When we get a upload URL, we dispatch a PUT reqeust to S3 for the file.
;; If there still are more files to upload, we dispatch again a fetch for upload URL.
;;
;; All files with an identical filename to an already uploaded file are excluded from the
;; upload process.
;;
;; This iterative upload avoids hitting the Lambda concurrency limit for upload URL fetch,
;; as we have environments (whimsical), which have a very low Lambda concurrency limit.
;;
;; On S3 PUT reqeust completion, we update the state of the file uploaded.
;; If the PUT was succesfull, we also update the aggregated :upload-state

(rf/reg-event-fx
  ::initiate-file-upload-process
  ;; "context" parameter currently pased only by document upload started by attachment,
  ;;  when attaching a file to questionnaire or to answer for a question in a questionnaire
  (fn [{:keys [db]} [_ existing-files file-type context]]
    (let [files-to-upload (subs/get-selected-files-sorted db file-type existing-files)]
      (if (not-empty files-to-upload)
        {:db (-> db
                 (assoc-in [:upload file-type :upload-status] :uploading)
                 (assoc-in [:upload file-type :context] context))
         :fx [[:dispatch [::fetch-upload-url file-type files-to-upload context]]]}
        {:db (assoc-in db [:ui :error] "Nothing to upload")}))))

(rf/reg-event-fx
 ::fetch-upload-url
 (fn [{:keys [db]} [_ file-type files context]]
   (let [[file-name {:keys [file]}] (first files)]
     {:db (assoc-in db [:upload file-type :files file-name :upload-status] :uploading)
      :http-xhrio {:method          :get
                   :uri             (str (case file-type
                                           :policies "/api/policies/files/"
                                           :questionnaires "/api/questionnaires/files/")
                                         (js/encodeURIComponent file-name) "/upload-url")
                   :timeout         http-config/timeout
                   :format          http-config/transit-request-format
                   :response-format http-config/transit-response-format
                   :on-success      [::fetch-next-upload-url file-type file-name file (next files) context]
                   :on-failure      [::set-file-upload-url-fetch-failure file-type file-name]}})))

(rf/reg-event-fx
  ::fetch-next-upload-url
  (fn [_ [_ file-type file-name file files context {:keys [url]}]]
    {:fx [(when files
            [:dispatch [::fetch-upload-url file-type files context]])
          [:dispatch [::put-file-to-s3 file-type file-name file url context]]]}))

(rf/reg-event-fx
  ::put-file-to-s3
  (fn [_ [_ file-type file-name file url context]]
    {:http-xhrio {:method          :put
                  :uri             url
                  :body            file
                  :response-format (ajax/text-response-format)
                  :on-success      [::s3-upload-success file-type file-name context]
                  :on-failure      [::set-s3-upload-error file-type file-name]}}))

(defn set-file-upload-status [db file-type file-name status]
  (assoc-in db [:upload file-type :files file-name :upload-status] status))

(rf/reg-event-fx
  ::set-s3-upload-error
  (fn [{:keys [db]} [_ file-type file-name error]]
    {:db (set-file-upload-status db file-type file-name :failed)
     :fx [[:dispatch [::ui/set-error error]]]}))

(rf/reg-event-fx
  ::s3-upload-success
  (fn [_ [_ file-type file-name context]]
    {:http-xhrio (cond-> {:method :post
                          :uri (case file-type
                                 :policies (str "/api/policies/files/" file-name)
                                 :questionnaires (str "/api/questionnaires/files/" (js/encodeURIComponent file-name)))
                          :timeout http-config/timeout
                          :format http-config/transit-request-format
                          :response-format http-config/transit-response-format
                          :on-failure [::ui/set-error "Failed to notify upload completion"]
                          :on-success [::refresh-state file-type file-name context]}

                   (= file-type :policies)
                   (assoc :params {:document-type (if (#{:attachment :answer} (:source context))
                                                    "attachment"
                                                    "policy")}))
     :fx (if (= file-type :questionnaires)
           [[:dispatch [::notifications/add {:message (str "Loading workbooks from " file-name)}]]]
           [])}))

(rf/reg-event-fx
  ::refresh-state
  (fn [{:keys [db]} [_ file-type file-name context {:keys [id]}]]
    (let [{:keys [answer-id source]} context ;; Note the context is null for questionnaire uploads
          db (let [db              (assoc-in db [:upload file-type :files file-name :upload-status] :completed)
                   files           (get-in db [:upload file-type :files])
                   upload-statuses (set (map :upload-status (vals files)))]
               (update-in db
                          [:upload file-type :upload-status]
                          (fn [status]
                            ;; If all uploads are done, change status to :completed, otherwise keep the the status as is
                            (if (= #{:completed} upload-statuses)
                              :completed
                              status))))]
      {:db db
       :fx (cond-> [[:dispatch (case file-type
                                 :policies [::documents/get]
                                 :questionnaires [::questionnaire-list/get])]]
             ;; If this was an attachment to questionnaire or an answer for a question in a questionnaire,
             ;; then add the attachment to the questionnaire
             (#{:attachment :answer} source)
             (conj (let [questionnaire-id        (get-in db [:page :questionnaire-detail :uuid])
                         answers (if answer-id
                                   #{answer-id}
                                   #{})]
                     [:dispatch [::questionnaire.db/add-attachment
                                 questionnaire-id
                                 {:type "document"
                                  :document-id id
                                  :filename file-name
                                  :linked-answers answers}]]))

             (= file-type :questionnaires)
             (conj [:dispatch [::notifications/add {:message (str "Workbooks loaded from " file-name)}]]))})))

(rf/reg-event-db
  ::set-file-upload-url-fetch-failure
  (fn [db [_ file-type file-name error]]
    (-> db
        (assoc-in [:upload file-type :upload-status] :failed)
        (set-file-upload-status file-type file-name :failed)
        ;; TODO: multiple files can fail, so errors clobber each other
        (assoc-in [:ui :error] error))))